import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';

import './index.css';
import { App } from './App';
import { ErrorFallback } from '@/components/common/ErrorFallback';
import { db } from '@/services/storage/db';
import { usePropertyStore } from '@/stores/property-store';
import { sentryService } from '@/services/sentry-service';
import { posthogService } from '@/services/posthog-service';

// ── Observability bootstrap (must run before render) ─────────────────────────
sentryService.init();
posthogService.init();

const rootElement = document.getElementById('root');
// SAFETY: Vite's index.html always includes <div id="root">, so this element is guaranteed present.
const root = createRoot(rootElement!);

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={({ error }) => <ErrorFallback error={error as Error} />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);

// Trigger async GMU backfill for properties migrated to Dexie v13.
// After resolving, reload the property store so the UI reflects the updated gmuStatus.
void db.runPendingGmuBackfill()
  .then(() => usePropertyStore.getState().loadProperties())
  .catch((err: unknown) => {
    console.error('[boot] gmu backfill failed', err);
  });

// Trigger async photo bytes migration for rows flagged by the v18 → v19 upgrade
// (ADR-044 §B). Non-blocking per offline-first principle — usePhotoSrc shows a
// 'migrating' placeholder for any row caught mid-migration and re-renders on
// the row-mutation event when its row completes.
void db.runPendingPhotoBytesMigration()
  .catch((err: unknown) => {
    console.error('[boot] photo bytes migration failed', err);
  });

// Request persistent storage so IndexedDB data survives browser storage pressure.
// This is a best-effort call — browsers may deny on first load but grant after install.
if (navigator.storage?.persist) {
  void navigator.storage.persist().then((persisted) => {
    if (!persisted) {
      console.info('Ridgen: storage persistence not granted — data may be evicted under pressure');
    }
  });
}
